/**
 * Shared utilities for DataGrid components
 * Contains common components, hooks, types, and styles used across LgmDataGrid and ProductsGroupedDataGrid
 */

import React, { useState, useRef, useEffect } from 'react';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid-premium';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  COLOR_DANGER_TEXT,
  COLOR_INFO_TEXT,
  COLOR_WARNING_TEXT,
} from 'theme/colors';

/**
 * Custom action interface for DataGrid action columns
 */
export interface CustomAction<T> {
  icon: React.ReactElement;
  onAction: (row: T) => void;
  title?: string;
  style?: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  showInMenu?: boolean;
}

/**
 * Cell component that shows a popover with full content when clicked if truncated
 */
export function TruncatedCell({ params }: { params: GridRenderCellParams }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cellRef.current) {
      const element = cellRef.current;
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [params.value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isTruncated) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const value = params.value ?? '';

  return (
    <>
      <div
        ref={cellRef}
        onClick={handleClick}
        style={{
          ...cellStyles.truncatedCellContainer,
          cursor: isTruncated ? 'pointer' : 'default',
        }}
        title={isTruncated ? 'Click to view full content' : undefined}>
        {String(value)}
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: cellStyles.popoverPaper,
          },
        }}>
        <Typography
          sx={{
            p: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
          {String(value)}
        </Typography>
      </Popover>
    </>
  );
}

/**
 * Hook to manage column resize enablement with delay to prevent timing issues
 */
export function useResizeDelay(loading: boolean, dataLength: number) {
  const [resizeEnabled, setResizeEnabled] = useState(false);

  useEffect(() => {
    if (!loading && dataLength > 0) {
      const timer = setTimeout(() => {
        setResizeEnabled(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setResizeEnabled(false);
    }
  }, [loading, dataLength]);

  return resizeEnabled;
}

/**
 * Hook to create a sort handler that prevents unsorted state
 * MUI DataGrid cycles: none -> ASC -> DESC -> none
 * We want to cycle: ASC -> DESC -> ASC (no unsorted state)
 */
export function useSortHandler(
  sortModel: GridSortModel,
  onSortModelChange?: (model: GridSortModel) => void
) {
  const handleSortModelChange = (model: GridSortModel) => {
    if (onSortModelChange) {
      if (model.length === 0 && sortModel.length > 0) {
        // User clicked to unsort, flip to opposite direction instead
        const currentSort = sortModel[0];
        const newDirection = currentSort.sort === 'asc' ? 'desc' : 'asc';
        onSortModelChange([{ field: currentSort.field, sort: newDirection }]);
      } else {
        onSortModelChange(model);
      }
    }
  };

  return handleSortModelChange;
}

/**
 * Calculate actions column configuration.
 *
 * `customActionsCount` must be the actual number of items `customActionsGetter`
 * returns (e.g. `customActionsGetter(sampleRow).length`) -- a caller passing a
 * hardcoded guess here silently clips extra custom actions (e.g. Manufacturers'
 * "View Facilities" + "View Products") once a second one is added.
 */
export function calculateActionsConfig<T>(
  onInfo?: (row: T) => void,
  onEdit?: (row: T) => void,
  onDelete?: (row: T) => void,
  onMerge?: (row: T) => void,
  customActionsGetter?: (row: T) => CustomAction<T>[],
  customActionsCount = 0
) {
  const hasActions = Boolean(
    onInfo || onEdit || onDelete || onMerge || customActionsGetter
  );

  const actionCount =
    (onInfo ? 1 : 0) +
    (onEdit ? 1 : 0) +
    (onDelete ? 1 : 0) +
    (onMerge ? 1 : 0);

  const totalActions = actionCount + customActionsCount;
  const actionsColumnWidth = Math.max(80, totalActions * 35 + 10); // Min 80px, ~40px per action + 30px padding

  return { hasActions, actionsColumnWidth };
}

/**
 * Shared cell styles
 */
export const cellStyles: Record<string, React.CSSProperties> = {
  truncatedCellContainer: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  popoverPaper: {
    maxWidth: '600px',
    maxHeight: '400px',
    overflow: 'auto',
  } as React.CSSProperties,
};

/**
 * Shared DataGrid styles
 */
export const dataGridStyles: SxProps<Theme> = {
  flex: 1,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #e0e0e0',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f9f9f9',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
    minHeight: '40px',
    maxHeight: '40px',
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: '40px',
    height: '40px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    margin: 0,
    fontSize: '0.75rem',
  },
  '& .MuiDataGrid-actionsCell': {
    gap: '8px',
    '& .MuiButtonBase-root:nth-of-type(1)': {
      color: '#2563eb',
      '&:hover': {
        color: COLOR_INFO_TEXT,
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(2)': {
      color: '#d97706',
      '&:hover': {
        color: COLOR_WARNING_TEXT,
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(3)': {
      color: '#dc2626',
      '&:hover': {
        color: COLOR_DANGER_TEXT,
        backgroundColor: 'transparent',
      },
    },
    '& .MuiButtonBase-root:nth-of-type(4)': {
      color: '#8b5cf6',
      '&:hover': {
        color: '#6d28d9',
        backgroundColor: 'transparent',
      },
    },
  },
};
